import { countMinMediaEligible, type OrderedStep } from "@instacover/helpers"

import { calculateIntroStepFinished } from "@/sections/intro/introUtils"
import { logger } from "@/utils/monitoring/logging"
import type { SessionClaimMarkerData, SessionConfig, SessionDetail, SessionPhoto, SessionVideo } from "@/utils/types"

import { dexie } from "../db"
import type { ConfigLocal } from "../tables/config/config"
import type { PhotoSlim } from "../tables/photos/photos"
import type { VideoItem } from "../tables/videos/videos"

type HasMovedPastStepParams = {
  laterSteps: OrderedStep[]
  photos: SessionPhoto[]
  videos: SessionVideo[]
}

/** True once the user moved past a step: a later step already holds media, skipped ones included */
function hasMovedPastStep(params: HasMovedPastStepParams): boolean {
  const { laterSteps, photos, videos } = params

  return laterSteps.some(
    (later) => photos.some((p) => p.type === later.name) || videos.some((v) => v.type === later.name),
  )
}

type DetermineIsFinishedParams = {
  step: OrderedStep
  /** Steps ordered after `step` — used to detect a skipped optional step */
  laterSteps: OrderedStep[]
  photos: SessionPhoto[]
  videos: SessionVideo[]
  claimData: SessionClaimMarkerData[]
}

function determineIsFinished(params: DetermineIsFinishedParams): boolean {
  const { step, laterSteps, photos, videos, claimData } = params
  const { stepType, name: stepName } = step

  switch (stepType) {
    case "VehiclePhoto":
    case "DocumentPhoto": {
      const stepPhotos = photos.filter((p) => p.type === stepName)

      // more than 1 photo required
      if (step.minMedia > 1) {
        const uploadedPhotos = stepPhotos.filter((p) => p.url !== undefined)
        return countMinMediaEligible(uploadedPhotos) >= step.minMedia
      }

      // 1 photo required or step is optional
      return stepPhotos.some((p) => p.url !== undefined || p.skipped)
    }
    case "VehicleVideo": {
      const stepVideos = videos.filter((v) => v.type === stepName)
      return stepVideos.some((v) => v.url !== undefined || v.skipped)
    }
    case "Claim": {
      const hasClaimPhoto = photos.some((p) => p.type === stepName)
      const isComplete =
        hasClaimPhoto && claimData.length > 0 && claimData.some((marker) => marker.claimDetails.length > 0)
      if (isComplete) return true

      // Mandatory step that isn't complete yet
      if (step.minMedia !== 0) return false

      // Optional step counts as finished only when it was skipped: no claim photo, but the user
      // already moved on to a later step that has a photo or video
      return !hasClaimPhoto && hasMovedPastStep({ laterSteps, photos, videos })
    }
    case "Damage":
    case "ExtraEquipment": {
      const stepPhotos = photos.filter((p) => p.type === stepName)

      // Mandatory step stays unfinished until enough photos actually arrived
      if (step.minMedia > 0) {
        return countMinMediaEligible(stepPhotos) >= step.minMedia
      }

      // Optional step with photos
      if (step.minMedia === 0 && stepPhotos.length > 0) {
        return true
      }

      // Optional step with nothing captured: only skip it once the user is demonstrably past it,
      // otherwise show it again so they get to decide
      return hasMovedPastStep({ laterSteps, photos, videos })
    }
    case "ExtraClientData":
      return false
  }
}

/**
 * Server photo → Dexie row. The server sends every field under the name Dexie stores it, so the payload
 * spreads in as-is and only the local-only fields are filled in here.
 */
function toPhotoRow(serverPhoto: SessionPhoto, sessionId: string, now: Date): Omit<PhotoSlim, "dexieId"> {
  return {
    ...serverPhoto,
    sessionId,
    updatedAt: now,
    uploadedAt: serverPhoto.createdAt,
    uploadProgress: 101,
    uploadFailed: false,
    // Server photos have no local binary — they render from the presigned URL
    hasData: false,
    hasThumbnailData: false,
  }
}

/** Server video → Dexie row. Same principle as `toPhotoRow`. */
function toVideoRow(serverVideo: SessionVideo, sessionId: string): Omit<VideoItem, "dexieId"> {
  return {
    ...serverVideo,
    sessionId,
    uploaded: 1,
    readyToUpload: 1,
    uploadProgress: 101,
    uploadFailed: false,
  }
}

type LoadServerDataToDexieParams = {
  sessionId: string
  detail: SessionDetail
  config: SessionConfig
  photos: SessionPhoto[]
  videos: SessionVideo[]
  claimData: SessionClaimMarkerData[]
}

export async function loadServerDataToDexie(params: LoadServerDataToDexieParams): Promise<void> {
  const { sessionId, detail, config, photos, videos, claimData } = params

  logger.debug("loadServerDataToDexie, photos: ", photos)

  const now = new Date()

  await dexie.transaction(
    "rw",
    [
      dexie.session,
      dexie.config,
      dexie.photos,
      dexie.videos,
      dexie.orderedSteps,
      dexie.claimMarkers,
      dexie.claimDetails,
    ],
    async () => {
      // ── Session upsert ──────────────────────────────────────────────
      const existingSession = await dexie.session.where("id").equals(detail.id).first()
      if (existingSession) {
        await dexie.session.update(existingSession.dexieId, { ...detail })
      } else {
        await dexie.session.add({ ...detail })
      }

      // ── Config upsert ──────────────────────────────────────────────
      const configData: Omit<ConfigLocal, "dexieId"> = {
        sessionId,
        theme: config.theme,
        logoUrl: config.logoUrl,
        languages: config.languages,
        onlyLandscape: config.onlyLandscape,
        customerId: config.customerId,
        customerName: config.customerName,
        organizationId: config.organizationId,
        organizationHumanReadableId: config.organizationHumanReadableId,
        organizationModelUrl: config.organizationModelUrl,
      }
      const existingConfig = await dexie.config.where("sessionId").equals(sessionId).first()
      if (existingConfig) {
        await dexie.config.update(existingConfig.dexieId, configData)
      } else {
        await dexie.config.add(configData)
      }

      // ── Photos: incremental sync ───────────────────────────────────
      const localPhotos = await dexie.photos.where("sessionId").equals(sessionId).toArray()
      const localPhotoMap = new Map(localPhotos.map((p) => [p.id, p]))

      for (const serverPhoto of photos) {
        const local = localPhotoMap.get(serverPhoto.id)

        // New photo from server — add
        if (!local) {
          await dexie.photos.add(toPhotoRow(serverPhoto, sessionId, now))
          continue
        }

        // Exists locally — only update if local doesn't have uploadedAt yet
        // and is not actively uploading (progress 0-100)
        if (!local.uploadedAt && local.uploadProgress === -1) {
          await dexie.photos.update(local.dexieId, {
            ...serverPhoto,
            updatedAt: now,
            uploadProgress: 101,
            uploadFailed: false,
            uploadedAt: serverPhoto.createdAt,
          })
        }
      }

      // ── Videos: incremental sync ───────────────────────────────────
      const localVideos = await dexie.videos.where("sessionId").equals(sessionId).toArray()
      const localVideoMap = new Map(localVideos.map((v) => [v.id, v]))

      for (const serverVideo of videos) {
        const local = localVideoMap.get(serverVideo.id)

        if (!local) {
          await dexie.videos.add(toVideoRow(serverVideo, sessionId))
          continue
        }

        if (!local.url && local.uploadProgress === -1) {
          await dexie.videos.update(local.dexieId, {
            url: serverVideo.url,
            uploaded: 1,
            uploadProgress: 101,
            uploadFailed: false,
            skipped: serverVideo.skipped,
          })
        }
      }

      // ── Claim markers + details: server hydration (add-if-new) ─────
      // Server rows have no local binary — details render from the presigned url.
      // Locally-pending (not-yet-uploaded) rows aren't returned by the server, so they're left untouched.
      const localMarkers = await dexie.claimMarkers.where("sessionId").equals(sessionId).toArray()
      const localMarkerIds = new Set(localMarkers.map((m) => m.id))

      for (const serverMarker of claimData) {
        if (!localMarkerIds.has(serverMarker.id)) {
          await dexie.claimMarkers.add({
            id: serverMarker.id,
            sessionId,
            photoId: serverMarker.sessionPhotoId,
            points: serverMarker.points,
            width: serverMarker.width,
            createdAt: serverMarker.createdAt,
            uploadedAt: serverMarker.createdAt,
          })
        }

        const localDetails = await dexie.claimDetails.where("markerId").equals(serverMarker.id).toArray()
        const localDetailIds = new Set(localDetails.map((d) => d.id))

        for (const serverDetail of serverMarker.claimDetails) {
          if (localDetailIds.has(serverDetail.id)) continue

          await dexie.claimDetails.add({
            id: serverDetail.id,
            sessionId,
            markerId: serverMarker.id,
            createdAt: serverDetail.createdAt,
            updatedAt: serverDetail.createdAt,
            hasData: false,
            url: serverDetail.url,
            uploadedAt: serverDetail.createdAt,
          })
        }
      }

      // ── Ordered steps: atomic insert with double-check ─────────────
      const stepsCount = await dexie.orderedSteps.where("sessionId").equals(sessionId).count()
      if (stepsCount > 0) return

      if (detail.consentScreenEnabled) {
        await dexie.orderedSteps.add({
          name: "GDPRConsent",
          sessionId,
          documentValidityCheck: false,
          enableUpload: false,
          minMedia: 1,
          maxMedia: 1,
          qualityCheck: false,
          stepType: "GDPRConsent",
          viewsCheck: false,
          isFinished: detail.consentScreenSeen,
        })
      }

      const isIntroFinished = calculateIntroStepFinished({ detail, photos, videos })
      await dexie.orderedSteps.add({
        name: "Intro",
        sessionId,
        documentValidityCheck: false,
        enableUpload: false,
        minMedia: 1,
        maxMedia: 1,
        qualityCheck: false,
        stepType: "Intro",
        viewsCheck: false,
        isFinished: isIntroFinished,
      })

      const hasDocumentSteps = config.orderedSteps.some((s) => s.stepType === "DocumentPhoto")
      const documentSteps = config.orderedSteps.filter((s) => s.stepType === "DocumentPhoto")
      let documentsIntroInserted = false

      for (const [index, orderedStep] of config.orderedSteps.entries()) {
        // Insert DocumentsIntro before the first DocumentPhoto step
        if (hasDocumentSteps && !documentsIntroInserted && orderedStep.stepType === "DocumentPhoto") {
          const hasAnyDocumentPhoto = documentSteps.some((s) => photos.some((p) => p.type === s.name))

          await dexie.orderedSteps.add({
            name: "DocumentsIntro",
            sessionId,
            documentValidityCheck: false,
            enableUpload: false,
            minMedia: 1,
            maxMedia: 1,
            qualityCheck: false,
            stepType: "DocumentsIntro",
            viewsCheck: false,
            isFinished: hasAnyDocumentPhoto,
          })

          documentsIntroInserted = true
        }

        const laterSteps = config.orderedSteps.slice(index + 1)
        const isFinished = determineIsFinished({ step: orderedStep, laterSteps, photos, videos, claimData })

        await dexie.orderedSteps.add({
          ...orderedStep,
          sessionId,
          isFinished,
        })
      }

      await dexie.orderedSteps.add({
        name: "FinalCheck",
        sessionId,
        isFinished: detail.finished,
        documentValidityCheck: false,
        enableUpload: false,
        minMedia: 1,
        maxMedia: 1,
        qualityCheck: false,
        stepType: "FinalCheck",
        viewsCheck: false,
      })
    },
  )
}
